import { useContext } from 'react';

import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import {
  Box,
  Button,
  styled as muiStyled,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { ConfirmDialog } from '../../../components/dialogs';
import { SearchField } from '../../../components/inputs';
import { Pagination } from '../../../components/paginations';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Spinner } from '../../../components/spinners';
import { Defaults } from '../../../constants';
import { useGetSubCategoriesByPage } from '../../../lib/category/hooks/useGetSubCategoriesByPage';
import { useCreateFeaturedBanner } from '../../../lib/featured-banner/hooks/useCreateFeaturedBanner';
import { SortDirection } from '../../../lib/util/helpers';
import { Category } from '../../../types/Category';
import { FeaturedBannerType } from '../../../types/FeaturedBanner';

const FeaturedBannerSubCategoryList = () => {
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    search: StringParam,
    sort: StringParam,
  });
  const { page, search, sort } = query;
  const searchString = search && { search };
  const debouncedSearchFn = useDebouncedCallback((value: string) => {
    setQuery({ search: value, page: 0 });
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const { data, isLoading, error } = useGetSubCategoriesByPage({
    page: Number(page) || 0,
    sort: String(sort || ''),
    ...searchString,
  });

  const { mutateAsync: createFeaturedBanner } = useCreateFeaturedBanner();
  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);

  const handleCreateFeaturedBanner = async (subCategory: Category) => {
    try {
      await createFeaturedBanner({ object_id: subCategory.id, type: FeaturedBannerType.SUB_CATEGORY });
    } catch (error) {
      setAlertSnackbar({
        message: (error as any).message || 'Failed to create featured banner',
        severity: 'error',
        vertical: 'top',
      });
    }
  };

  const setNewSort = (column: string, direction: SortDirection) => {
    const newSort = direction ? `${column} ${direction}` : undefined;
    setQuery({ sort: newSort });
  };

  const setNewPage = (page: number) => setQuery({ page: page - 1 });

  const sortToObj = (sortStr: string | undefined) => {
    const initialValues = { column: '', direction: undefined };
    if (typeof sortStr !== 'string') {
      return initialValues;
    }
    const [column, direction] = sortStr.split(' ');
    if (typeof column !== 'string' || !['asc', 'desc', undefined].includes(direction)) {
      return initialValues;
    }
    return { ...initialValues, column, direction: direction as SortDirection };
  };

  const sortObj = sortToObj(sort ?? '');

  const columns = [
    {
      text: 'Name',
      name: 'category.title',
    },
    {
      text: 'Image',
      name: 'category.image',
    },
    {
      text: 'Actions',
      name: '',
    },
  ];

  return (
    <Container>
      <FullWidthBar>
        <StyledSearchField
          searchString={search ?? ''}
          onSearch={(search: string) => {
            debouncedSearchFn(search);
          }}
        />
      </FullWidthBar>

      <TableContainer component={Paper}>
        <Table aria-labelledby="tableTitle">
          <TableHead>
            <PointerTableRow>
              {columns.map((column) => (
                <TableCell key={column.name}>
                  <TableSortLabel
                    active={sortObj.column === column.name}
                    direction={sortObj.direction}
                    onClick={() => {
                      const isAsc = sortObj.column === column.name && sortObj.direction === 'asc';
                      setNewSort(column.name, isAsc ? 'desc' : 'asc');
                    }}
                    component="a"
                  >
                    {column.text}
                    {sortObj.column === column.name ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortObj.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </PointerTableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !error &&
              data &&
              data.data.map((row: any, index: number) => {
                return (
                  <StyledTableRow key={index} className={row.deleted ? 'deleted-row' : 'row'}>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>
                      {row.media ? (
                        <img src={row.media.mediumUrl} alt={row.title} style={{ height: '100px', width: '150px', objectFit: 'cover' }} />
                      ) : (
                        <img
                          src="/images/empty-image.png"
                          alt="Placeholder"
                          style={{ height: '100px', width: '150px', objectFit: 'cover' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <ConfirmDialog
                        trigger={(open) => <Button onClick={open}>Select</Button>}
                        title="Add to Featured Banner"
                        description={`Are you sure you want to add "${row.title}" to featured banners?`}
                        onConfirm={async (close) => {
                          await handleCreateFeaturedBanner(row);
                          close();
                        }}
                        cancelBtnText="Cancel"
                        submitBtnText="Add"
                      />
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            {data?.data?.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={3} style={{ textAlign: 'center' }}>
                  No sub-categories records found.
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data?.total !== 0 && (
        <FullWidthBar>
          <Typography variant="subtitle1">Total: {data?.total}</Typography>
          <StyledPagination
            current={Number(page) + 1 || 1}
            total={Math.ceil(Number(data?.total) / Defaults.LIST_PAGE_SIZE)}
            onPageChange={(page: number) => setNewPage(page)}
          />
        </FullWidthBar>
      )}
    </Container>
  );
};

const Container = styled('div')({
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
});

const FullWidthBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PointerTableRow = styled(TableRow)({
  cursor: 'pointer',
});

const StyledTableRow = muiStyled(PointerTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledPagination = styled(Pagination)({
  alignSelf: 'flex-end',
});

const StyledSearchField = styled(SearchField)({
  marginLeft: 'auto',
});

export default FeaturedBannerSubCategoryList;
